// sicBoActions.js
'use strict';
import {
  MERGE_OPERATION_DATA,
  REMOVE_OPERATION_DATA,
  REMOVE_LIST_ITEMS,
} from '../ActionTypes.js';
import { GAME_NEXT_SESSION_THRESHOLD_SECONDS } from '../RemoteConfigKeys.js';

import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import getOperationData from '../selector/getOperationData.js';
import getGameData from '../selector/getGameData.js';
import getListData from '../selector/getListData.js';
import getTimestampOffset, {
  Accuracy,
} from '../selector/getTimestampOffset.js';

import {
  GameStateEnum,
  GameStateDurationSecondEnum,
  GAME_DURATION_MSEC,
} from '../resource/gameSicBo.js';
import { game as gameDebug } from '../resource/debug.js';

export const GAME_STATUS_TIMER_SELECT_PATH = [
  'timer',
  'sicBo',
  'gameStatusTimer',
];

const gameDebugLog = gameDebug.extend('log:action/sicBoActions');

/**
 * Enter game
 * @kind action
 * @return {Promise} Action promise.
 */
export const enterGame = () => async (dispatch, getState) => {
  const log = gameDebugLog.extend('enterGame');
  const channelName = 'private-enc-campaign@sic_bo';
  const isChannelAuthenticated = getOperationData(
    getState(),
    ['pusher', 'channels', channelName],
    'authenticated'
  );
  const currentSicBoId = getOperationData(
    getState(),
    ['game', 'sicBo'],
    'currentSicBoId'
  );
  const currentGameStatus = getOperationData(
    getState(),
    ['game', 'sicBo'],
    'currentStatus'
  );
  if (
    currentSicBoId ||
    GameStateEnum.WAITING_GAME_ID !== currentGameStatus ||
    !isChannelAuthenticated
  ) {
    log(
      'current sic bo id is not empty or game status is not in waiting game id or channel not authenticated',
      {
        currentGameStatus,
        currentSicBoId,
        isChannelAuthenticated,
      }
    );
    return dispatch({ type: '' });
  }
  const timestampOffset = getTimestampOffset(getState(), Accuracy.MILLISECOND);
  const nextGameThresholdSeconds = getRemoteConfigData(
    getState(),
    GAME_NEXT_SESSION_THRESHOLD_SECONDS
  );
  const now = Date.now() + timestampOffset;
  const nowUnix = Math.floor(now / 1000);
  const games = (getListData(getState(), ['game', 'sicBo'], 'itemIds') || [])
    .map(id => getGameData(getState(), [id]))
    .filter(item => !!item)
    .sort((a, b) => a.exp - b.exp);
  const validGame = games.find(item => {
    const { nbf } = item;
    const sessionEndTimestamp = nbf * 1000 + GAME_DURATION_MSEC;
    const isInGameSession = now < sessionEndTimestamp;
    const shouldGoNextGameSession =
      now >= sessionEndTimestamp - nextGameThresholdSeconds * 1000;
    if (isInGameSession && !shouldGoNextGameSession) {
      return true;
    }
    return false;
  });
  if (!validGame) {
    log('not found valid sic bo game', {
      games,
      nowUnix,
    });
    return dispatch({ type: '' });
  }
  log('found valid sic bo game', { game: validGame, nowUnix });
  const currentActiveChip = getOperationData(
    getState(),
    ['game', 'sicBo'],
    'activeChip'
  );
  dispatch({
    type: MERGE_OPERATION_DATA,
    payload: {
      selectPath: ['game', 'sicBo'],
      data: {
        currentSicBoId: validGame.id,
        enterGameTimestamp: now,
        activeChip: currentActiveChip == null ? 100 : currentActiveChip,
      },
    },
  });
  return dispatch(setGameState({ sicBoId: validGame.id }));
};

/**
 * Set game state
 * @kind action
 * @param {string} {sicBoId} - sic bo id.
 * @return {Promise} Action promise.
 */
// TODO: refactor this action
export const setGameState =
  ({ sicBoId } = {}) =>
  async (dispatch, getState) => {
    const log = gameDebugLog.extend('setGameState');
    const nbf = getGameData(getState(), [sicBoId], 'nbf');
    const exp = getGameData(getState(), [sicBoId], 'exp');
    const timestampOffset = getTimestampOffset(
      getState(),
      Accuracy.MILLISECOND
    );
    const now = Date.now() + timestampOffset;
    const nowUnix = Math.floor(now / 1000);
    const enterGameTimestamp = getOperationData(
      getState(),
      ['game', 'sicBo'],
      'enterGameTimestamp'
    );
    const isEnterGameAfterBetting = enterGameTimestamp >= exp * 1000;

    let delay;
    let targetTimestamp;

    const currentGameStatus = getOperationData(
      getState(),
      ['game', 'sicBo'],
      'currentStatus'
    );
    const currentSicBoId = getOperationData(
      getState(),
      ['game', 'sicBo'],
      'currentSicBoId'
    );
    log('execute setGameState', {
      sicBoId,
      nbf,
      exp,
      currentGameStatus,
      currentSicBoId,
      enterGameUnixTimestamp: Math.floor(enterGameTimestamp / 1000),
      nowUnix,
    });
    if (GameStateEnum.WAITING_GAME_ID === currentGameStatus) {
      if (isEnterGameAfterBetting) {
        // from waiting to rolling
        const nextStatus = GameStateEnum.ROLLING;
        const duration = GameStateDurationSecondEnum.ROLLING;
        const startTimestamp = now;
        const endTimestamp = now + duration * 1000;
        targetTimestamp = endTimestamp;
        delay = endTimestamp - now;
        log(`state change from ${currentGameStatus} to ${nextStatus}`);
        dispatch({
          type: MERGE_OPERATION_DATA,
          payload: {
            selectPath: ['game', 'sicBo'],
            data: {
              currentStatus: nextStatus,
              statusStartTimestamp: startTimestamp,
              statusEndTimestamp: endTimestamp,
            },
          },
        });
      } else {
        // from waiting to preparing
        const nextStatus = GameStateEnum.PREPARING;
        const duration = GameStateDurationSecondEnum.PREPARING;
        const startTimestamp = now;
        const endTimestamp = now + duration * 1000;
        targetTimestamp = endTimestamp;
        delay = endTimestamp - now;
        log(`state change from ${currentGameStatus} to ${nextStatus}`);
        dispatch({
          type: MERGE_OPERATION_DATA,
          payload: {
            selectPath: ['game', 'sicBo'],
            data: {
              currentStatus: nextStatus,
              statusStartTimestamp: startTimestamp,
              statusEndTimestamp: endTimestamp,
            },
          },
        });
      }
    } else if (GameStateEnum.PREPARING === currentGameStatus) {
      // from preparing to betting
      const nextStatus = GameStateEnum.BETTING;
      const startTimestamp = now;
      const endTimestamp = exp * 1000;
      targetTimestamp = endTimestamp;
      delay = endTimestamp - now;
      log('delay', {
        now,
        timestampOffset,
        nowUnix: Math.floor(now / 1000),
        nbf,
        exp,
      });
      log(`state change from ${currentGameStatus} to ${nextStatus}`);
      dispatch({
        type: MERGE_OPERATION_DATA,
        payload: {
          selectPath: ['game', 'sicBo'],
          data: {
            currentStatus: nextStatus,
            statusStartTimestamp: startTimestamp,
            statusEndTimestamp: endTimestamp,
          },
        },
      });
    } else if (GameStateEnum.BETTING === currentGameStatus) {
      // from betting to rolling
      const nextStatus = GameStateEnum.ROLLING;
      const duration = GameStateDurationSecondEnum.ROLLING;
      const startTimestamp = now;
      const endTimestamp = now + duration * 1000;
      targetTimestamp = endTimestamp;
      delay = endTimestamp - now;
      log(`state change from ${currentGameStatus} to ${nextStatus}`);
      dispatch({
        type: MERGE_OPERATION_DATA,
        payload: {
          selectPath: ['game', 'sicBo'],
          data: {
            currentStatus: nextStatus,
            statusStartTimestamp: startTimestamp,
            statusEndTimestamp: endTimestamp,
          },
        },
      });
    } else if (GameStateEnum.ROLLING === currentGameStatus) {
      // from rolling to drawing
      const results = getGameData(getState(), [sicBoId], 'results') || [];
      if (!results.length) {
        log('game results not exist', {
          sicBoId,
          now,
          game: getGameData(getState(), [sicBoId]),
          currentSicBoId: getOperationData(
            getState(),
            ['game', 'sicBo'],
            'currentSicBoId'
          ),
          currentStatus: getOperationData(
            getState(),
            ['game', 'sicBo'],
            'currentStatus'
          ),
          statusStartTimestamp: getOperationData(
            getState(),
            ['game', 'sicBo'],
            'statusStartTimestamp'
          ),
          statusEndTimestamp: getOperationData(
            getState(),
            ['game', 'sicBo'],
            'statusEndTimestamp'
          ),
        });
        return dispatch({ type: '' });
        // dispatch(setGameState({ sicBoId }));
      }
      const nextStatus = GameStateEnum.DRAWING;
      const duration = GameStateDurationSecondEnum.DRAWING;
      const startTimestamp = now;
      const endTimestamp = now + duration * 1000;
      targetTimestamp = endTimestamp;
      delay = endTimestamp - now;
      log(`state change from ${currentGameStatus} to ${nextStatus}`);
      dispatch({
        type: MERGE_OPERATION_DATA,
        payload: {
          selectPath: ['game', 'sicBo'],
          data: {
            currentStatus: nextStatus,
            statusStartTimestamp: startTimestamp,
            statusEndTimestamp: endTimestamp,
          },
        },
      });
    } else if (GameStateEnum.DRAWING === currentGameStatus) {
      // from drawing to resetting
      const nextStatus = GameStateEnum.RESETTING;
      const duration = GameStateDurationSecondEnum.RESETTING;
      const startTimestamp = now;
      const endTimestamp = now + duration * 1000;
      targetTimestamp = endTimestamp;
      delay = endTimestamp - now;
      log(`state change from ${currentGameStatus} to ${nextStatus}`);
      dispatch({
        type: MERGE_OPERATION_DATA,
        payload: {
          selectPath: ['game', 'sicBo'],
          data: {
            currentStatus: nextStatus,
            statusStartTimestamp: startTimestamp,
            statusEndTimestamp: endTimestamp,
          },
        },
      });
    } else if (GameStateEnum.RESETTING === currentGameStatus) {
      // from resetting to finished
      const nextStatus = GameStateEnum.FINISHED;
      const duration = GameStateDurationSecondEnum.FINISHED;
      const startTimestamp = now;
      const endTimestamp = now + duration * 1000;
      targetTimestamp = endTimestamp;
      delay = endTimestamp - now;
      log(`state change from ${currentGameStatus} to ${nextStatus}`);
      dispatch({
        type: MERGE_OPERATION_DATA,
        payload: {
          selectPath: ['game', 'sicBo'],
          data: {
            currentStatus: nextStatus,
            statusStartTimestamp: startTimestamp,
            statusEndTimestamp: endTimestamp,
          },
        },
      });
    } else if (GameStateEnum.FINISHED === currentGameStatus) {
      // from finished to waiting game id
      const nextStatus = GameStateEnum.WAITING_GAME_ID;
      log(`state change from ${currentGameStatus} to ${nextStatus}`);
      dispatch({
        type: MERGE_OPERATION_DATA,
        payload: {
          selectPath: ['game', 'sicBo'],
          data: {
            currentSicBoId: undefined,
            currentStatus: nextStatus,
            statusStartTimestamp: undefined,
            statusEndTimestamp: undefined,
          },
        },
      });
      dispatch({
        type: REMOVE_LIST_ITEMS,
        payload: {
          selectPath: ['game', 'sicBo'],
          itemIds: [sicBoId],
        },
      });
    }

    if (delay != null) {
      log(`state change in ${delay}ms`);
      clearTimeout(
        getOperationData(getState(), GAME_STATUS_TIMER_SELECT_PATH, 'timerId')
      );
      const timerId = setTimeout(() => {
        dispatch({
          type: REMOVE_OPERATION_DATA,
          payload: {
            selectPath: GAME_STATUS_TIMER_SELECT_PATH,
          },
        });
        dispatch(setGameState({ sicBoId }));
      }, delay);
      dispatch({
        type: MERGE_OPERATION_DATA,
        payload: {
          selectPath: GAME_STATUS_TIMER_SELECT_PATH,
          data: {
            timerId,
            targetTimestamp,
            delay,
          },
        },
      });
    }
  };

/**
 * Close game sic bo.
 * @kind action
 * @return {Promise} Action promise.
 */
export const closeGameSicBo = () => async (dispatch, getState) => {
  const log = gameDebugLog.extend('closeGameSicBo');
  log('execute closeGameSicBo');
  dispatch({
    type: REMOVE_OPERATION_DATA,
    payload: { selectPath: ['game', 'sicBo'] },
  });
  clearTimeout(
    getOperationData(getState(), GAME_STATUS_TIMER_SELECT_PATH, 'timerId')
  );
  dispatch({
    type: REMOVE_OPERATION_DATA,
    payload: { selectPath: GAME_STATUS_TIMER_SELECT_PATH },
  });
  const sicBoIds = getListData(getState(), ['game', 'sicBo'], 'itemIds') || [];
  return dispatch({
    type: REMOVE_LIST_ITEMS,
    payload: {
      selectPath: ['game', 'sicBo'],
      itemIds: sicBoIds,
    },
  });
};
